/**
 * 系统管理页面 — 表格样式
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, AlertTriangle } from 'lucide-react';
import { api, getErrorMessage } from '@/services/api';
import { useToast } from '@/components/Toast';
import styles from './Roles.module.css';

interface SysConfig { system_key: string; system_name: string; enabled: boolean; }
interface RoleConfig { system_key: string; role_id: number; role_name: string; enabled: boolean; }

const SYS_ORDER = ['exam', 'knowledge', 'share', 'work'];

export default function SystemConfigPage() {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [systems, setSystems] = useState<SysConfig[]>([]);
  const [roles, setRoles] = useState<RoleConfig[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [toggling, setToggling] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{show:boolean;type:string;key:string;name:string;enabled:boolean}|null>(null);

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await api.request<{systems:SysConfig[];roles:RoleConfig[]}>('/system-config/');
      const sys = (res.data?.systems||[]).filter(s => s.system_key !== 'admin');
      sys.sort((a,b) => SYS_ORDER.indexOf(a.system_key) - SYS_ORDER.indexOf(b.system_key));
      setSystems(sys);
      setRoles(res.data?.roles||[]);
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const requestToggle = (type: string, key: string, name: string, enabled: boolean) => {
    setConfirmModal({ show: true, type, key, name, enabled });
  };

  const doToggle = async () => {
    if (!confirmModal) return;
    const { type, key, enabled } = confirmModal;
    const newState = !enabled;
    setToggling(key);
    try {
      if (type === 'system') {
        await api.request(`/system-config/systems/${key}`, { method:'PUT', body:JSON.stringify({enabled:newState}) });
        setSystems(prev => prev.map(s => s.system_key===key?{...s,enabled:newState}:s));
        if (!newState) setRoles(prev => prev.map(r => r.system_key===key?{...r,enabled:false}:r));
        else loadConfig();
      } else {
        await api.request(`/system-config/roles/${key}`, { method:'PUT', body:JSON.stringify({enabled:newState}) });
        setRoles(prev => prev.map(r => r.role_id===Number(key)?{...r,enabled:newState}:r));
      }
      showToast('success', `${newState?'启用':'禁用'}成功`);
    } catch (err) { showToast('error', getErrorMessage(err)); }
    finally { setToggling(null); setConfirmModal(null); }
  };

  if (loading) return <div style={{display:'flex',justifyContent:'center',padding:60}}><Loader2 size={24}/></div>;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>系统管理</h1>
          <p className={styles.subtitle}>管理各功能系统的启用状态及可用角色。变更将立即生效。</p>
        </div>
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>系统名称</th>
              <th className={styles.centerCell}>状态</th>
              <th className={styles.centerCell}>可用角色</th>
              <th className={styles.centerCell}>操作</th>
            </tr>
          </thead>
          <tbody>
            {systems.map(sys => {
              const sysRoles = roles.filter(r => r.system_key === sys.system_key);
              const enabledRoles = sysRoles.filter(r => r.enabled);
              return (
                <motion.tr key={sys.system_key} initial={{opacity:0}} animate={{opacity:1}}>
                  <td>
                    <span style={{fontSize:'.9375rem',fontWeight:600,color:'var(--ink-900)'}}>{sys.system_name}</span>
                  </td>
                  <td className={styles.centerCell}>
                    <span style={{fontSize:'.8125rem',fontWeight:500,padding:'3px 12px',borderRadius:12,
                      background:sys.enabled?'#d1fae5':'#fee2e2',color:sys.enabled?'#059669':'#dc2626'}}>
                      {sys.enabled ? '运行中' : '已禁用'}
                    </span>
                  </td>
                  <td className={styles.centerCell}>
                    <div style={{display:'flex',flexWrap:'wrap',gap:4,justifyContent:'center'}}>
                      {sysRoles.map(role => (
                        <button key={role.role_id}
                          onClick={() => requestToggle('role', String(role.role_id), role.role_name, role.enabled)}
                          disabled={toggling===String(role.role_id)}
                          style={{padding:'3px 10px',borderRadius:6,border:`1.5px solid ${role.enabled?'var(--cinnabar-400)':'#e5e7eb'}`,
                            background:role.enabled?'#fef3f2':'#f9fafb',color:role.enabled?'var(--cinnabar-600)':'#9ca3af',
                            fontSize:'.75rem',fontWeight:role.enabled?500:400,cursor:'pointer',whiteSpace:'nowrap'}}>
                          {role.role_name}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className={styles.centerCell}>
                    <button onClick={() => requestToggle('system', sys.system_key, sys.system_name, sys.enabled)}
                      disabled={toggling===sys.system_key}
                      style={{padding:'5px 14px',borderRadius:16,border:'none',cursor:'pointer',fontSize:'.75rem',fontWeight:500,
                        background:sys.enabled?'#fee2e2':'#d1fae5',color:sys.enabled?'#dc2626':'#059669'}}>
                      {toggling===sys.system_key?<Loader2 size={14}/>:sys.enabled?'禁用':'启用'}
                    </button>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {confirmModal && (
          <motion.div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:3000,display:'flex',alignItems:'center',justifyContent:'center'}}
            initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} onClick={()=>setConfirmModal(null)}>
            <motion.div style={{background:'white',borderRadius:16,padding:28,maxWidth:440,width:'90%'}}
              initial={{scale:.95}} animate={{scale:1}} exit={{scale:.95}} onClick={e=>e.stopPropagation()}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
                <AlertTriangle size={24} color={confirmModal.enabled?'#f59e0b':'#ef4444'}/>
                <h3 style={{fontSize:'1rem',fontWeight:700,margin:0}}>
                  {confirmModal.enabled?`禁用「${confirmModal.name}」`:`启用「${confirmModal.name}」`}
                </h3>
              </div>
              <p style={{fontSize:'.875rem',color:'#6b7280',lineHeight:1.6,marginBottom:16}}>
                {confirmModal.enabled
                  ? (confirmModal.type==='system'?`禁用后所有用户将无法访问「${confirmModal.name}」的任何功能。`:`禁用后拥有「${confirmModal.name}」角色的用户将无法使用对应系统。`)
                  : `启用后将恢复相关用户的访问权限。`}
              </p>
              <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                <button onClick={()=>setConfirmModal(null)} style={{padding:'8px 16px',border:'1px solid #e5e7eb',borderRadius:8,background:'white',cursor:'pointer',fontSize:'.8125rem'}}>取消</button>
                <button onClick={doToggle} style={{padding:'8px 20px',border:'none',borderRadius:8,background:confirmModal.enabled?'#ef4444':'#10b981',color:'white',cursor:'pointer',fontSize:'.8125rem',fontWeight:500}}>
                  {confirmModal.enabled?'确认禁用':'确认启用'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
